/**
 * DeveloperEmailAdminPage.jsx · v2.7.45+E13 (Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Developer-Only Email Admin Dashboard. STRICTLY restricted to
 * canonical_role ∈ {developer, admin}. The route component itself
 * gates access at the top — non-developers see a stark "Access denied"
 * card with no further data hydration.
 *
 * Three sub-sections (tab switch):
 *   1. Account Assignments — link global mailboxes (dispatch@, support@,
 *      …) to specific user accounts.
 *   2. Signature Locks — force a signature onto a user and prevent them
 *      from editing or removing it.
 *   3. Positions — bulk-assign positions[] across users.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import { useAdmin } from "@/contexts/AdminContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldAlert, Mail, Lock, Unlock, ArrowLeft, Tags, Plus, X, Loader2,
  Crown, Code2, Users, FileText, Search, ShieldCheck, Copy, Trash2, EyeOff,
  Wand2, Power, PowerOff, RefreshCw,
} from "lucide-react";
import PositionMultiSelect from "@/components/admin/PositionMultiSelect";
import CapabilitiesMatrix from "@/components/admin/CapabilitiesMatrix";
import PromptStudioPanel from "@/components/admin/PromptStudioPanel";
import { canManageDeveloperConsole } from "@/lib/positions";
import { BISON_CORPORATE_EMAIL_LOGO, TAXI_COMMANDER_LOGO } from "@/lib/brandAssets";

export default function DeveloperEmailAdminPage() {
  // v2.7.45+E13.1 · AdminContext exposes `admin`, not `user`. Renaming the
  // destructure target keeps the variable name `user` semantically clear
  // within this file (the page treats the caller as "the user looking at
  // the dashboard") while pulling from the correct context key.
  const { admin: user } = useAdmin();
  const canonical = (user?.canonical_role || user?.role || "").toLowerCase();
  const allowed = canManageDeveloperConsole(canonical);

  if (!allowed) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 p-8">
        <div className="max-w-2xl mx-auto bg-white dark:bg-zinc-900 border-2 border-rose-300 dark:border-rose-900 rounded-lg p-8 text-center" data-testid="dev-admin-denied">
          <ShieldAlert className="h-12 w-12 text-rose-600 dark:text-rose-400 mx-auto mb-3"/>
          <h2 className="text-xl font-bold text-rose-900 dark:text-rose-100">Email Admin credentials required</h2>
          <p className="text-sm text-slate-600 dark:text-zinc-400 mt-2 leading-relaxed">
            This dashboard is restricted to accounts with Operating Partner, Developer, or master Admin role.
            Email account and signature assignments must flow through these tiers to ensure
            consistent global policy across the platform.
          </p>
          <p className="text-xs text-slate-500 dark:text-zinc-500 mt-3">
            Your current role: <b>{canonical || "unknown"}</b>
          </p>
          <Button asChild variant="outline" className="mt-4 gap-1.5">
            <Link to="/admin"><ArrowLeft className="h-3.5 w-3.5"/>Back to Admin Console</Link>
          </Button>
        </div>
      </div>
    );
  }

  return <DeveloperEmailAdminBody />;
}

function DeveloperEmailAdminBody() {
  const nav = useNavigate();
  const [tab, setTab] = useState("accounts");
  const [data, setData] = useState({ users: [], accounts: [], signatures: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/dev/email-assignments");
      setData(r.data || { users: [], accounts: [], signatures: [] });
    } catch (e) {
      toast.error(`Failed to load: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return data.users;
    const t = search.trim().toLowerCase();
    return data.users.filter((u) =>
      (u.email || "").toLowerCase().includes(t) ||
      (u.name  || "").toLowerCase().includes(t)
    );
  }, [data.users, search]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-950">
      {/* v2.7.48+E23 · Dual-logo header (Taxi Commander + Bison Email)
          Symmetrical 50 % size reduction · side-by-side, centered. */}
      <div
        className="bg-slate-50 dark:bg-zinc-950 flex items-center justify-center gap-4 sm:gap-6 md:gap-8 border-b border-slate-200 dark:border-zinc-800 overflow-hidden"
        style={{ padding: 0, margin: 0 }}
        data-testid="dev-admin-bison-brand"
      >
        <img
          src={TAXI_COMMANDER_LOGO}
          alt="Taxi Commander · Admin Console Software"
          className="block h-16 sm:h-20 md:h-[100px] w-auto object-contain"
          style={{ maxWidth: "50%", margin: 0, padding: 0, display: "block" }}
          data-testid="dev-admin-taxi-commander-brand"
        />
        <img
          src={BISON_CORPORATE_EMAIL_LOGO}
          alt="Bison Software · Corporate Email System"
          className="block h-16 sm:h-20 md:h-[100px] w-auto object-contain"
          style={{ maxWidth: "50%", margin: 0, padding: 0, display: "block" }}
        />
      </div>
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 px-5 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => nav("/admin")} className="gap-1.5 -ml-2" data-testid="dev-admin-back">
            <ArrowLeft className="h-4 w-4"/>Admin Console
          </Button>
          <div className="h-6 w-px bg-slate-300 dark:bg-zinc-700"/>
          <div className="flex items-center gap-2 min-w-0">
            <Code2 className="h-5 w-5 text-violet-600 dark:text-violet-400 flex-shrink-0"/>
            <div className="min-w-0">
              <div className="font-bold text-lg truncate">Email Admin</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-violet-700 dark:text-violet-300">
                Operating Partner · Developer · Admin only
              </div>
            </div>
          </div>
        </div>
        <Button onClick={reload} variant="outline" size="sm" disabled={loading} data-testid="dev-admin-refresh" className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Users className="h-3.5 w-3.5"/>}
          Refresh
        </Button>
      </div>

      {/* Tab bar */}
      <div className="border-b border-slate-200 dark:border-zinc-800 px-5 bg-white dark:bg-zinc-950 flex items-center gap-1 overflow-x-auto">
        {[
          { id: "accounts",     label: "Account Assignments", Icon: Mail },
          { id: "signatures",   label: "Signature Locks",     Icon: Lock },
          { id: "positions",    label: "Positions",            Icon: Tags },
          { id: "capabilities", label: "Capabilities Matrix",  Icon: ShieldCheck },
          { id: "dedup",        label: "Email Dedup",          Icon: Copy },
          { id: "system",       label: "System Controls",      Icon: Power }, // v2.7.58+E43
          { id: "prompts",      label: "Prompt Studio",        Icon: Wand2 },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 transition-colors whitespace-nowrap
              ${tab === t.id
                ? "border-violet-600 text-violet-700 dark:text-violet-300"
                : "border-transparent text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-zinc-100"}`}
            data-testid={`dev-tab-${t.id}`}
          >
            <t.Icon className="h-4 w-4"/>{t.label}
          </button>
        ))}
      </div>

      <div className="p-5 max-w-7xl mx-auto">
        {/* User search bar (only relevant for user-keyed tabs) */}
        {tab !== "capabilities" && tab !== "dedup" && tab !== "prompts" && tab !== "system" && (
          <div className="mb-4 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"/>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by user name or email…"
                className="pl-9 h-9 text-sm"
                data-testid="dev-admin-search"
              />
            </div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500">
              {filteredUsers.length} of {data.users.length} users
            </div>
          </div>
        )}

        {tab === "accounts" && (
          <AccountAssignmentsTable users={filteredUsers} accounts={data.accounts} onChange={reload}/>
        )}
        {tab === "signatures" && (
          <SignatureLocksTable users={filteredUsers} signatures={data.signatures} onChange={reload}/>
        )}
        {tab === "positions" && (
          <PositionsTable users={filteredUsers} onChange={reload}/>
        )}
        {tab === "capabilities" && (
          <CapabilitiesMatrix/>
        )}
        {tab === "dedup" && (
          <EmailDedupPanel/>
        )}
        {tab === "system" && (
          <EmailSystemControlPanel/>
        )}
        {tab === "prompts" && (
          <PromptStudioPanel/>
        )}
      </div>
    </div>
  );
}

// ── Account Assignments ─────────────────────────────────────────────
function AccountAssignmentsTable({ users, accounts, onChange }) {
  const [busy, setBusy] = useState({});
  const [picker, setPicker] = useState({}); // {[userId]: accountId-to-add}

  const assign = async (userId, accountId) => {
    if (!accountId) return;
    setBusy((b) => ({ ...b, [userId]: true }));
    try {
      await adminApi.post("/admin/dev/email-assignments/assign", { user_id: userId, account_id: accountId });
      toast.success("Mailbox linked");
      setPicker((p) => ({ ...p, [userId]: "" }));
      onChange();
    } catch (e) {
      toast.error(`Assign failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  };
  const unassign = async (userId, accountId) => {
    setBusy((b) => ({ ...b, [userId]: true }));
    try {
      await adminApi.post("/admin/dev/email-assignments/unassign", { user_id: userId, account_id: accountId });
      toast.success("Mailbox unlinked");
      onChange();
    } catch (e) {
      toast.error(`Unassign failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  };

  return (
    <div className="space-y-3" data-testid="dev-accounts-table">
      <div className="text-xs text-slate-600 dark:text-zinc-400 bg-violet-50 dark:bg-violet-900/15 border border-violet-200 dark:border-violet-900/40 rounded p-3 leading-relaxed">
        Link global mailboxes (e.g. <b>dispatch@</b>, <b>support@</b>, <b>partner@</b>, <b>driver@</b>) to specific user accounts.
        Regular users have <b>no</b> ability to add, remove, or edit these assignments — every change must flow through a Developer.
      </div>
      {users.map((u) => {
        const assignedIds = new Set(u.assigned_account_ids || []);
        const remaining = accounts.filter((a) => !assignedIds.has(a.id));
        const sel = picker[u.id] || "";
        return (
          <div key={u.id} className="border border-slate-200 dark:border-zinc-800 rounded-lg p-3 bg-white dark:bg-zinc-900/40" data-testid={`dev-user-${u.id}`}>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="font-bold text-sm flex items-center gap-2 min-w-0">
                  {(u.canonical_role || "").toLowerCase() === "admin" && <Crown className="h-3.5 w-3.5 text-amber-500 flex-shrink-0"/>}
                  <span className="truncate">{u.name || u.email}</span>
                </div>
                <div className="text-[11px] text-slate-500 dark:text-zinc-500">{u.email} · {u.role}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Select value={sel} onValueChange={(v) => setPicker((p) => ({ ...p, [u.id]: v }))}>
                  <SelectTrigger className="h-8 w-56 text-xs" data-testid={`dev-account-picker-${u.id}`}>
                    <SelectValue placeholder={remaining.length === 0 ? "All accounts linked" : "Link a mailbox…"}/>
                  </SelectTrigger>
                  <SelectContent>
                    {remaining.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.email}{a.is_alias && " (alias)"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="bg-violet-600 hover:bg-violet-500 text-white h-8 gap-1 text-xs"
                  disabled={!sel || busy[u.id]}
                  onClick={() => assign(u.id, sel)}
                  data-testid={`dev-account-assign-${u.id}`}>
                  <Plus className="h-3 w-3"/>Link
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(u.assigned_account_ids || []).length === 0 && (
                <div className="text-[11px] text-slate-500 dark:text-zinc-500 italic">No mailboxes linked.</div>
              )}
              {(u.assigned_account_ids || []).map((aid) => {
                const a = accounts.find((x) => x.id === aid);
                if (!a) return null;
                return (
                  <span key={aid} className="text-[11px] px-2 py-0.5 rounded bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300 flex items-center gap-1 font-mono">
                    <Mail className="h-3 w-3"/>
                    {a.email}
                    <button type="button" onClick={() => unassign(u.id, aid)} disabled={busy[u.id]}
                      className="hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5 ml-0.5" aria-label={`Unlink ${a.email}`}
                      data-testid={`dev-account-unassign-${u.id}-${aid}`}>
                      <X className="h-2.5 w-2.5"/>
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
      {users.length === 0 && (
        <div className="text-center py-10 text-sm text-slate-500 dark:text-zinc-500 italic">No users match the filter.</div>
      )}
    </div>
  );
}

// ── Signature Locks ─────────────────────────────────────────────────
function SignatureLocksTable({ users, signatures, onChange }) {
  const [busy, setBusy] = useState({});
  const [picker, setPicker] = useState({});

  const lock = async (userId, sigId) => {
    if (!sigId) return;
    setBusy((b) => ({ ...b, [userId]: true }));
    try {
      await adminApi.post("/admin/dev/signature-assignments/lock", { user_id: userId, signature_id: sigId });
      toast.success("Signature locked onto user");
      onChange();
    } catch (e) {
      toast.error(`Lock failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  };
  const unlock = async (userId, sigId) => {
    setBusy((b) => ({ ...b, [userId]: true }));
    try {
      await adminApi.post("/admin/dev/signature-assignments/unlock", { user_id: userId, signature_id: sigId });
      toast.success("Lock released");
      onChange();
    } catch (e) {
      toast.error(`Unlock failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  };

  return (
    <div className="space-y-3" data-testid="dev-signatures-table">
      <div className="text-xs text-slate-600 dark:text-zinc-400 bg-violet-50 dark:bg-violet-900/15 border border-violet-200 dark:border-violet-900/40 rounded p-3 leading-relaxed">
        Lock a built signature onto a specific user — once locked, the targeted user can <b>neither edit nor delete</b> the signature.
        Only a Developer can release the lock. Each user can carry one locked signature at a time (latest lock wins).
      </div>
      {users.map((u) => {
        const lockedSig = signatures.find((s) => s.id === u.locked_signature_id);
        const sel = picker[u.id] || "";
        return (
          <div key={u.id} className="border border-slate-200 dark:border-zinc-800 rounded-lg p-3 bg-white dark:bg-zinc-900/40">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="font-bold text-sm truncate">{u.name || u.email}</div>
                <div className="text-[11px] text-slate-500 dark:text-zinc-500">{u.email}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {lockedSig ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] px-2 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 flex items-center gap-1 font-bold">
                      <Lock className="h-3 w-3"/>{lockedSig.name}
                    </span>
                    <Button size="sm" variant="outline" disabled={busy[u.id]} onClick={() => unlock(u.id, lockedSig.id)}
                      className="h-8 gap-1 text-xs" data-testid={`dev-sig-unlock-${u.id}`}>
                      <Unlock className="h-3 w-3"/>Release lock
                    </Button>
                  </div>
                ) : (
                  <>
                    <Select value={sel} onValueChange={(v) => setPicker((p) => ({ ...p, [u.id]: v }))}>
                      <SelectTrigger className="h-8 w-56 text-xs" data-testid={`dev-sig-picker-${u.id}`}>
                        <SelectValue placeholder="Lock a signature…"/>
                      </SelectTrigger>
                      <SelectContent>
                        {signatures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_default && " (default)"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="bg-violet-600 hover:bg-violet-500 text-white h-8 gap-1 text-xs"
                      disabled={!sel || busy[u.id]}
                      onClick={() => lock(u.id, sel)}
                      data-testid={`dev-sig-lock-${u.id}`}>
                      <Lock className="h-3 w-3"/>Lock
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {users.length === 0 && (
        <div className="text-center py-10 text-sm text-slate-500 dark:text-zinc-500 italic">No users match the filter.</div>
      )}
    </div>
  );
}

// ── Positions ───────────────────────────────────────────────────────
function PositionsTable({ users, onChange }) {
  const [busy, setBusy] = useState({});
  const [drafts, setDrafts] = useState({});

  const save = async (userId) => {
    const positions = drafts[userId];
    if (!positions) return;
    setBusy((b) => ({ ...b, [userId]: true }));
    try {
      const r = await adminApi.patch(`/admin/users/${userId}/positions`, { positions });
      toast.success(`Positions saved · role auto-set to ${r.data?.canonical_role}`);
      setDrafts((d) => { const c = { ...d }; delete c[userId]; return c; });
      onChange();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  };

  return (
    <div className="space-y-3" data-testid="dev-positions-table">
      <div className="text-xs text-slate-600 dark:text-zinc-400 bg-violet-50 dark:bg-violet-900/15 border border-violet-200 dark:border-violet-900/40 rounded p-3 leading-relaxed">
        Assign one or more positions to each user. The user&apos;s RBAC tier (driver / dispatcher / supervisor / partner / developer / admin)
        is automatically recomputed from the highest-privilege position. Users <b>cannot</b> edit their own positions —
        management is restricted to Partner, Developer, and Admin accounts.
      </div>
      {users.map((u) => {
        const draft = drafts[u.id] ?? u.positions ?? [];
        const dirty = JSON.stringify((draft || []).slice().sort()) !== JSON.stringify((u.positions || []).slice().sort());
        return (
          <div key={u.id} className="border border-slate-200 dark:border-zinc-800 rounded-lg p-3 bg-white dark:bg-zinc-900/40">
            <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
              <div className="min-w-0">
                <div className="font-bold text-sm truncate">{u.name || u.email}</div>
                <div className="text-[11px] text-slate-500 dark:text-zinc-500">{u.email} · current RBAC: <b className="text-violet-700 dark:text-violet-300">{u.canonical_role}</b></div>
              </div>
              {dirty && (
                <Button size="sm" onClick={() => save(u.id)} disabled={busy[u.id]}
                  className="bg-violet-600 hover:bg-violet-500 text-white h-8 gap-1 text-xs" data-testid={`dev-positions-save-${u.id}`}>
                  {busy[u.id] ? <Loader2 className="h-3 w-3 animate-spin"/> : <FileText className="h-3 w-3"/>}
                  Save positions
                </Button>
              )}
            </div>
            <PositionMultiSelect
              value={draft}
              onChange={(next) => setDrafts((d) => ({ ...d, [u.id]: next }))}
              canEdit={true}
              testidPrefix={`dev-positions-${u.id}`}
            />
          </div>
        );
      })}
      {users.length === 0 && (
        <div className="text-center py-10 text-sm text-slate-500 dark:text-zinc-500 italic">No users match the filter.</div>
      )}
    </div>
  );
}


// ── v2.7.45+E15 · Email Deduplication panel ─────────────────────────
// Lists duplicate email_accounts groups (same email registered more than
// once) and lets Partners / Developers disable or delete the dupes. The
// oldest row in every group is preserved automatically.
function EmailDedupPanel() {
  const [data, setData] = useState({ groups: [], total_groups: 0, total_duplicate_rows: 0 });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [selected, setSelected] = useState({}); // {email: bool}

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/email/duplicates");
      setData(r.data || { groups: [], total_groups: 0, total_duplicate_rows: 0 });
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const selectedEmails = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const allSelected = data.groups.length > 0 && data.groups.every((g) => selected[g.email]);

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else {
      const next = {};
      data.groups.forEach((g) => { next[g.email] = true; });
      setSelected(next);
    }
  };

  const run = async (action) => {
    const emails = selectedEmails;
    if (emails.length === 0) {
      toast.error("Select at least one duplicate group first.");
      return;
    }
    const confirm = action === "delete"
      ? window.confirm(`PERMANENTLY DELETE ${emails.length} duplicate group(s)?\n\nThe survivor (oldest row) of each group is kept. This cannot be undone.`)
      : window.confirm(`Disable duplicates in ${emails.length} group(s)?\n\nThe survivor (oldest row) of each group is left active. Disabled rows can be re-enabled later.`);
    if (!confirm) return;
    setActing(true);
    try {
      const r = await adminApi.post("/admin/email/duplicates", { action, emails });
      toast.success(`${action === "delete" ? "Deleted" : "Disabled"} ${r.data?.affected || 0} row(s)`);
      setSelected({});
      reload();
    } catch (e) {
      toast.error(`${action} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="dev-tab-dedup-body">
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-300 dark:border-amber-900/40 rounded p-3 text-xs text-amber-900 dark:text-amber-200" data-testid="dedup-warning">
        <b>Heads up:</b> these tools modify the local <code className="font-mono text-[10px]">email_accounts</code> table only.
        The matching IONOS mailboxes are <i>never</i> touched. &quot;Disable&quot; is reversible; &quot;Delete&quot; is permanent.
        The oldest row in every duplicate group is always preserved as the survivor.
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm">
          <b className="text-base">{data.total_groups}</b> duplicate group{data.total_groups === 1 ? "" : "s"}
          <span className="text-slate-500 dark:text-zinc-500"> · {data.total_duplicate_rows} extra row{data.total_duplicate_rows === 1 ? "" : "s"} that can be cleaned</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={reload} variant="outline" size="sm" disabled={loading} className="gap-1.5 h-8 text-xs" data-testid="dedup-refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <Search className="h-3 w-3"/>}Rescan
          </Button>
          <Button onClick={() => run("disable")} disabled={acting || selectedEmails.length === 0}
            className="bg-amber-600 hover:bg-amber-500 text-white h-8 text-xs gap-1.5" data-testid="dedup-disable-btn">
            <EyeOff className="h-3 w-3"/>Disable {selectedEmails.length > 0 ? `(${selectedEmails.length})` : ""}
          </Button>
          <Button onClick={() => run("delete")} disabled={acting || selectedEmails.length === 0}
            className="bg-rose-600 hover:bg-rose-500 text-white h-8 text-xs gap-1.5" data-testid="dedup-delete-btn">
            <Trash2 className="h-3 w-3"/>Delete {selectedEmails.length > 0 ? `(${selectedEmails.length})` : ""}
          </Button>
        </div>
      </div>

      {data.groups.length === 0 ? (
        <div className="text-center py-10 text-sm text-slate-500 dark:text-zinc-500 italic border border-dashed border-slate-300 dark:border-zinc-800 rounded" data-testid="dedup-empty">
          {loading ? "Scanning…" : "No duplicate email addresses found."}
        </div>
      ) : (
        <div className="border border-slate-200 dark:border-zinc-800 rounded overflow-hidden">
          <div className="bg-slate-100 dark:bg-zinc-900 px-3 py-2 flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="dedup-select-all"/>
            <span className="flex-1">Email · Group</span>
            <span className="w-20 text-right">Count</span>
          </div>
          {data.groups.map((g) => (
            <details key={g.email} className="border-t border-slate-200 dark:border-zinc-800 group" data-testid={`dedup-group-${g.email}`}>
              <summary className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-zinc-900/50 select-none">
                <input
                  type="checkbox"
                  checked={!!selected[g.email]}
                  onChange={(e) => setSelected((s) => ({ ...s, [g.email]: e.target.checked }))}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`dedup-select-${g.email}`}
                />
                <span className="flex-1 font-mono text-xs truncate">{g.email}</span>
                <span className="w-20 text-right text-sm font-bold text-amber-700 dark:text-amber-300">{g.count}×</span>
              </summary>
              <div className="px-6 pb-3 pt-1 bg-slate-50/50 dark:bg-zinc-900/30 text-xs">
                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-300 mb-1">
                  Survivor (kept) · Oldest row
                </div>
                <div className="text-xs mb-3 font-mono text-emerald-800 dark:text-emerald-200" data-testid={`dedup-survivor-${g.email}`}>
                  {g.survivor.label || g.survivor.id} · {g.survivor.created_at || "—"} · active={String(g.survivor.active)}
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700 dark:text-rose-300 mb-1">
                  Duplicates ({g.duplicates.length})
                </div>
                <div className="space-y-1">
                  {g.duplicates.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 font-mono text-xs text-rose-800 dark:text-rose-300" data-testid={`dedup-dup-${d.id}`}>
                      <span className="flex-1 truncate">
                        {d.label || d.id} · {d.created_at || "—"} · active={String(d.active)}
                      </span>
                      {/* v2.7.58+E43 · Per-row hard-delete of an individual duplicate
                          without touching sibling duplicates or the survivor. */}
                      <Button
                        onClick={async () => {
                          if (!window.confirm(`Permanently delete this one duplicate row?\n\n${d.label || d.id}\ncreated ${d.created_at || "—"}\n\nSurvivor row is untouched.`)) return;
                          try {
                            await adminApi.post("/admin/email/duplicates", {
                              action: "delete",
                              emails: [g.email],
                              row_ids: [d.id],
                            });
                            toast.success("Duplicate row deleted");
                            reload();
                          } catch (e) {
                            toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[10px] border-rose-400/50 text-rose-600 hover:bg-rose-500/10 gap-1"
                        data-testid={`dedup-delete-row-${d.id}`}
                      >
                        <Trash2 className="h-3 w-3"/>Delete
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}


// ── v2.7.58+E43 · Email System Controls (inbound/outbound kill-switch) ──
// Global toggle. When outbound is OFF, services.email_smtp.send_mail_sync
// short-circuits with skipped=True. When inbound is OFF the IMAP poller
// no-ops. Every skip is logged into email_gate_audit for the "Recent
// Skips" list rendered at the bottom of this panel.
function EmailSystemControlPanel() {
  const [cfg, setCfg] = useState(null);
  const [skips, setSkips] = useState([]);
  const [inMemory, setInMemory] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/email/system");
      setCfg(r.data?.config || null);
      setSkips(r.data?.recent_skips || []);
      setInMemory(r.data?.in_memory || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load email system config");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, next) => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/email/system/toggle", { [key]: next });
      setCfg(r.data);
      setInMemory({
        outbound_enabled: !!r.data?.outbound_enabled,
        inbound_enabled:  !!r.data?.inbound_enabled,
      });
      toast.success(`${key.replace("_enabled", "")} → ${next ? "ENABLED" : "DISABLED"}`);
      // Refresh skip log too
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusy(false); }
  };

  if (!cfg) {
    return (
      <div className="py-10 text-center text-slate-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin inline mr-1"/>Loading system controls…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="dev-tab-system-body">
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-300 dark:border-amber-900/40 rounded p-3 text-xs text-amber-900 dark:text-amber-200">
        <b>Global kill-switch.</b> When <code className="font-mono text-[10px]">outbound_enabled</code> is OFF, every SMTP send across the platform (driver notifications, delivery status pings, escalation alerts, marketing broadcasts, …) short-circuits with a silent-drop and audit row. When <code className="font-mono text-[10px]">inbound_enabled</code> is OFF, the IMAP pollers no-op on their next cycle.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ToggleCard
          label="Outbound Emails"
          desc="All SMTP sends across the platform (notifications, alerts, marketing)"
          enabled={!!cfg.outbound_enabled}
          onChange={(v) => toggle("outbound_enabled", v)}
          busy={busy}
          testid="email-system-outbound"
        />
        <ToggleCard
          label="Inbound Emails"
          desc="IMAP fetches from every provisioned mailbox"
          enabled={!!cfg.inbound_enabled}
          onChange={(v) => toggle("inbound_enabled", v)}
          busy={busy}
          testid="email-system-inbound"
        />
      </div>

      <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-zinc-500 flex items-center justify-between">
        <span>Last updated {cfg.updated_at ? new Date(cfg.updated_at).toLocaleString() : "—"} by {cfg.updated_by || "—"}</span>
        <button
          type="button"
          onClick={load}
          className="text-violet-600 dark:text-violet-300 hover:underline flex items-center gap-1"
          data-testid="email-system-refresh"
        ><RefreshCw className="h-3 w-3"/>Refresh</button>
      </div>

      {inMemory && (
        <div className="text-[11px] text-slate-500 dark:text-zinc-500">
          In-memory cache: outbound={inMemory.outbound_enabled ? "ON" : "OFF"} · inbound={inMemory.inbound_enabled ? "ON" : "OFF"}
        </div>
      )}

      <div>
        <div className="text-sm font-bold text-slate-700 dark:text-zinc-200 mb-2">Recent skipped sends</div>
        {skips.length === 0 ? (
          <div className="text-xs text-slate-500 dark:text-zinc-500 py-4 text-center border border-dashed border-slate-300 dark:border-zinc-700 rounded">
            No skipped sends recorded — either the kill-switch was never engaged or no outbound was attempted while OFF.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="email-system-skip-table">
              <thead className="bg-slate-100 dark:bg-zinc-900 text-[10px] uppercase tracking-widest text-slate-500 dark:text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Direction</th>
                  <th className="text-left px-3 py-2">To</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {skips.map((s, i) => (
                  <tr key={`${s.at}-${i}`} className="border-t border-slate-200 dark:border-zinc-800">
                    <td className="px-3 py-2 text-slate-500 dark:text-zinc-500 whitespace-nowrap">{s.at ? new Date(s.at).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 uppercase text-[10px] font-bold">
                      <span className={`px-1.5 py-0.5 rounded ${
                        s.direction === "outbound" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                                    : "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300"
                      }`}>{s.direction}</span>
                    </td>
                    <td className="px-3 py-2 font-mono">{s.to || "—"}</td>
                    <td className="px-3 py-2 truncate max-w-xs">{s.subject || "—"}</td>
                    <td className="px-3 py-2 text-rose-600 dark:text-rose-300 font-mono">{s.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleCard({ label, desc, enabled, onChange, busy, testid }) {
  return (
    <div
      className={`p-4 rounded-lg border-2 transition-colors ${
        enabled
          ? "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-400/60"
          : "bg-rose-50 dark:bg-rose-900/10 border-rose-400/60"
      }`}
      data-testid={`${testid}-card`}
    >
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2">
          {enabled
            ? <Power className="h-5 w-5 text-emerald-600 dark:text-emerald-300"/>
            : <PowerOff className="h-5 w-5 text-rose-600 dark:text-rose-300"/>}
          <div className="font-bold text-slate-900 dark:text-zinc-100">{label}</div>
        </div>
        <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded ${
          enabled
            ? "bg-emerald-400 text-zinc-950"
            : "bg-rose-400 text-zinc-950"
        }`} data-testid={`${testid}-pill`}>
          {enabled ? "Enabled" : "Disabled"}
        </span>
      </div>
      <div className="text-[11px] text-slate-500 dark:text-zinc-500 mb-3">{desc}</div>
      <Button
        onClick={() => onChange(!enabled)}
        disabled={busy}
        variant="outline"
        size="sm"
        className={`w-full h-9 font-bold text-xs ${
          enabled ? "text-rose-600 hover:bg-rose-500/10 border-rose-400/40"
                  : "text-emerald-600 hover:bg-emerald-500/10 border-emerald-400/40"
        }`}
        data-testid={`${testid}-toggle`}
      >
        {busy
          ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/>Working…</>
          : enabled
            ? <><PowerOff className="h-3.5 w-3.5 mr-1"/>Turn OFF</>
            : <><Power    className="h-3.5 w-3.5 mr-1"/>Turn ON</>}
      </Button>
    </div>
  );
}
